import React from "react";
import { Navigate } from "react-router";
import { Page, QueryResult } from "@sscs/ui";
import { getQueryStatus } from "@sscs/utils";
import { FORBIDDEN_ROUTE, PERMISSION } from "Constants";
import { useAllowedTo, useCurrentOrganization } from "Hooks";
import { useGetOrganizationUsageQuery } from "Services";
import Entitlements from "../../components/Entitlements/Entitlements";
import GroupUsage from "../../components/GroupUsage/GroupUsage";
import Header from "../../components/Header/Header";
import WarningBox from "../../components/WarningBox/WarningBox";
import { getHumanReadableOrgLimits } from "../../utils";
import styles from "./licensing.module.scss";

const Licensing = (): React.JSX.Element | null => {
  const currentOrganization = useCurrentOrganization();

  // not VIEW_ORGANIZATION_MONTHLY_CAPACITY_AND_USAGE: that permission also backs the
  // upload quota bars, so trial members hold it while they must not reach this page
  const canViewAnalysis = useAllowedTo({
    permissions: [PERMISSION.VIEW_MONTHLY_USAGE_PER_GROUP],
  });
  const canManageAnalysis = useAllowedTo({
    permissions: [PERMISSION.REALLOCATE_QUOTAS],
  });

  const { data, isError, isFetching, isLoading, error } =
    useGetOrganizationUsageQuery(
      {
        organizationId: currentOrganization?.id || "",
      },
      { skip: !currentOrganization || !canViewAnalysis },
    );
  const orgLimits = getHumanReadableOrgLimits(data);
  const portalAnalysisCapacity = data?.allocation_capacity_bytes_portal ?? 0;
  const hasWarning =
    orgLimits.groupLimitsSum > portalAnalysisCapacity ||
    orgLimits.hasGroupsWithAndWithoutLimit;

  if (!canViewAnalysis) {
    return <Navigate to={`/${FORBIDDEN_ROUTE}`} replace />;
  }

  return (
    <Page.Root>
      <Page.Header
        title={`${canManageAnalysis ? "Manage" : "View"} Analysis capacity`}
      />
      <QueryResult
        status={getQueryStatus({
          data,
          isError,
          isFetching,
          isLoading,
          error,
        })}
      >
        <Page.Body>
          <div className={styles.stack}>
            <div className={styles.overview}>
              {hasWarning && canManageAnalysis && (
                <WarningBox
                  hasGroupsWithAndWithoutLimit={
                    orgLimits.hasGroupsWithAndWithoutLimit
                  }
                  groupLimitsExceedAnalysisCapacity={
                    orgLimits.groupLimitsSum > portalAnalysisCapacity
                  }
                />
              )}
              <Header data={data} />
              <Entitlements data={data?.entitlements} />
            </div>
            <GroupUsage organizationUsage={data} />
          </div>
        </Page.Body>
      </QueryResult>
    </Page.Root>
  );
};

export default Licensing;
